import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@app/app.hook'
import { selectActiveModules, selectStrings } from '@app/slices/slice.app'

import { MODULE_TABLE } from '@app/app.config'
import { TOASTIFY_DEFAULT_OPTIONS } from '@app/app.constants'
import { getErrorText } from '@app/app.method'
import { selectToken } from '@app/slices/slice.token'
import { useInitializeSidebarVisibility } from '@login/MutationProvider/initializeSidebarVisibility'
import { useRevalidateToken } from '@login/MutationProvider/revalidateToken'
import { useValidateAPIPath } from '@login/MutationProvider/validateAPIPath'
import { useValidateRoute } from '@login/MutationProvider/validateRoute'

import {
    MODULE_VERSION,
    TREATMENT_FORMIK_INITIAL_VALUES,
    TREATMENT_VALIDATION_SCHEMA
} from '@fmt/constants'
import {
    GetTreatmentCarepathsResponse,
    GetTreatmentResponse,
    TreatmentParams,
    UpdateProfilePictureTreatmentResponse
} from '@fmt/type'
import { ImageDisplay } from '@profile/styles/ImageDisplay'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'

import { selectRouter } from '@app/app.store'
import { TokenData } from '@app/types/type.token'
import {
    useEditTreatmentMutation,
    useGetTreatmentCarepathsMutation,
    useGetTreatmentMutation,
    useUpdateProfilePictureTreatmentMutation
} from '@fmt/api'
import DashboardResponsiveHeader from '@fmt/components/DashboardResponsiveHeader'
import CarepathsViewTable from '@fmt/components/treatments/details/CarepathsTable'
import TreatmentName from '@fmt/components/treatments/details/inputs/Name'
import { back, push } from '@lagunovsky/redux-react-router'

import _ from 'lodash'
import { useParams } from 'react-router-dom'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'

const ProfilePic = ({
    showRecentlyUploaded,
    updateProfilePictureTreatmentMutation,
    getTreatmentMutation
}: {
    showRecentlyUploaded: boolean
    updateProfilePictureTreatmentMutation: {
        data: UpdateProfilePictureTreatmentResponse | undefined
    }
    getTreatmentMutation: {
        data?: GetTreatmentResponse | undefined
    }
}) => {
    const url = showRecentlyUploaded
        ? updateProfilePictureTreatmentMutation.data?.data.url
        : getTreatmentMutation.data?.data.profilePicture
    const imageThumbnail = url
        ? <ImageDisplay
            url={url}
            className={'rounded-square profile'}
        />
        : <div
            className={[
                'img-placeholder rounded-square profile'
            ].join(' ')}
        >
        </div>

    return imageThumbnail
}

const TreatmentDetailsInterface = () => {
    const dispatch = useAppDispatch()
    const strings = useAppSelector(selectStrings)

    const activeModules = useAppSelector(selectActiveModules)

    const revalidateToken = useRevalidateToken()
    const token = useAppSelector(selectToken)
    const validateAPIPath = useValidateAPIPath()
    const router = useAppSelector(selectRouter)
    const validateRoute = useValidateRoute()
    const initializeSidebarVisibility = useInitializeSidebarVisibility()

    // upload profile picture logic.
    const [file, setFile] = useState<File | null>(null)
    const [getTreatment, getTreatmentMutation] = useGetTreatmentMutation()
    const fixedCacheKey = 'details-getTreatmentCarepaths-key'
    const [getTreatmentCarepaths, getTreatmentCarepathsMutation] =
    useGetTreatmentCarepathsMutation({
        fixedCacheKey
    })
    const [currentPage, setCurrentPage] = useState(0)

    // a workaround to prevent tables from flickering. simple but it works.
    const [
        getTreatmentCarepathsResponse,
        setGetTreatmentCarepathsResponse
    ] = useState<GetTreatmentCarepathsResponse>()

    useEffect(() => {
        if (getTreatmentCarepathsMutation.data) {
            setGetTreatmentCarepathsResponse(getTreatmentCarepathsMutation.data)
        }
    }, [getTreatmentCarepathsMutation.data])

    const [showRecentlyUploaded, setShowRecentlyUploaded] = useState<boolean>(false)
    const [showCancelWarning, setShowCancelWarning] = useState<boolean>(false)

    const { treatmentId } = useParams<TreatmentParams>()

    const [editTreatment, editTreatmentMutation] = useEditTreatmentMutation()

    const [mode, setMode] = useState<'EDIT' | 'VIEW'>(
        _.lowerCase(router.location.pathname).includes('edit') ? 'EDIT' : 'VIEW'
    )

    // a fix that does the ff things:
    // before this existed, the getPatients gets called twice from two separate useEffects
    // the first hook should only be done once you clicked on either buttons that
    // changes the url. ideally, a quick solution. should be set to FALSE.
    const [modeButtonClicked, setModeButtonClicked] = useState(false)

    useEffect(() => {
        initializeSidebarVisibility(true)
    }, [])

    const treatmentEditFormik = useFormik({
        initialValues: getTreatmentMutation.data?.data || TREATMENT_FORMIK_INITIAL_VALUES,
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: TREATMENT_VALIDATION_SCHEMA(
            strings.app?.message.error.empty || ''
        ),
        onSubmit: (values) => {
            const call = async () => {
                if (token.valid) {
                    const newToken = await revalidateToken(
                        {
                            value: token.value,
                            id: token.id
                        },
                        token.mode
                    )
                    const foundApiPath = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths.editTreatment.path,
                        true
                    )

                    if (foundApiPath && newToken.value) {
                        editTreatment({
                            authToken: newToken.value,
                            data: {
                                treatmentId: treatmentId || '',
                                treatmentName: values.treatmentName || ''
                            }
                        })
                    }
                }
            }
            call()
        }
    })

    /** create fetch data function */
    const fetchData = (token: TokenData) => {
        let isMounted = true

        const call = async () => {
            if (
                token.valid &&
                treatmentId
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getTreatmentId.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        getTreatment({
                            authToken: newToken.value,
                            data: {
                                treatmentId
                            }
                        })
                    }

                    const isValid2 = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getTreatmentCarepaths.path,
                        true
                    )

                    if (isValid2 && newToken.value) {
                        getTreatmentCarepaths({
                            authToken: newToken.value,
                            data: {
                                treatmentId
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
        }
    }

    // THIS FETCH SHOULD NOT HAPPEN ON MOUNT.
    // solution change dependency to a useState. a button where you clicked on it
    // and then unset it.
    useEffect(() => {
        // should only be performed IF the button is clicked.
        if (modeButtonClicked) {
            setMode(
                _.lowerCase(router.location.pathname).includes('edit') ? 'EDIT' : 'VIEW'
            )

            // you go here ideally after submitting the values from the edit mode.
            if (_.lowerCase(router.location.pathname).includes('view')) {
                treatmentEditFormik.resetForm()
                // and then fetch the data again just to double check.
                // you only want to do this IF you came from clicking the button.
                fetchData(token)
            }

            // don't forget to set it false after.
            setModeButtonClicked(false)
        }
    }, [router.location.pathname, modeButtonClicked])
    // ONLY fetch it when needAccordion is true AND if isSuccess is false.
    useEffect(() => {
        if (treatmentId) {
            setShowRecentlyUploaded(false)
            return fetchData(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid, treatmentId])

    useEffect(() => {
        if (getTreatmentMutation.data) {
            const data = getTreatmentMutation.data
            treatmentEditFormik.setValues({
                ...treatmentEditFormik.values,
                ...data.data
            })
        }
    }, [getTreatmentMutation.data])

    useEffect(() => {
        if (getTreatmentMutation.error) {
            const message = getErrorText(getTreatmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [getTreatmentMutation.error])

    useEffect(() => {
        if (getTreatmentCarepathsMutation.error) {
            const message = getErrorText(getTreatmentCarepathsMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [getTreatmentCarepathsMutation.error])

    useEffect(() => {
        const data = editTreatmentMutation.data

        if (data) {
            if (data.status === 'OK') {
                // close component by clearing the edit formik.
                // treatmentEditFormik.resetForm()
                toast.success(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })

                fetchData(token)
            } else {
                toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
            }
        }
    }, [editTreatmentMutation.data])

    useEffect(() => {
        if (editTreatmentMutation.error) {
            const message = getErrorText(editTreatmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [editTreatmentMutation.error])

    const [updateProfilePictureTreatment, updateProfilePictureTreatmentMutation] =
    useUpdateProfilePictureTreatmentMutation()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (selectedFile) {
            setFile(selectedFile)
        }
    }

    const handleUpload = async () => {
        if (file) {
            const formData = new FormData()
            formData.append('profilePicture', file)

            const newToken = await revalidateToken({
                value: token.value,
                id: token.id
            }, token.mode)

            const foundApiPath = validateAPIPath(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.apiPaths.updateProfilePictureTreatment.path,
                true
            )

            if (foundApiPath) {
                updateProfilePictureTreatment({
                    treatmentId: treatmentId || '',
                    data: formData,
                    authToken: newToken.value
                }).unwrap().then((data) => {
                    if (data.status === 'OK') {
                    // toast success message.
                        toast.success(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                        // we need to use this token now so set it.
                        setShowRecentlyUploaded(true)
                    } else {
                        toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                    }
                })
            }
        }
    }

    useEffect(() => {
        handleUpload()
    }, [file])

    useEffect(() => {
        if (updateProfilePictureTreatmentMutation.error) {
            const message = getErrorText(updateProfilePictureTreatmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [updateProfilePictureTreatmentMutation.error])

    // const handleKeyDown = (
    //     e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    //     fieldName?: TreatmentKeys
    // ) => {
    //     if (e.key === 'Enter') {
    //         e.preventDefault()
    //         const nextInput = document.querySelector(`[name=${ fieldName }]`) as HTMLInputElement
    //         if (nextInput) {
    //             nextInput.focus()
    //             nextInput.select()
    //         } else {
    //             treatmentEditFormik.handleSubmit()
    //         }
    //     }
    // }

    const NameInput = useMemo(() => {
        return <TreatmentName readOnly={mode === 'VIEW'}
            treatmentEditFormik={treatmentEditFormik}
            handleKeyDown={(e) => {
                if (e.key === 'Enter') { treatmentEditFormik.handleSubmit() }
            }} />
    }, [
        mode,
        strings,
        treatmentEditFormik.values.treatmentName,
        treatmentEditFormik.errors.treatmentName
    ])

    const CancelButton = <button type={'button'}
        className={[
            'btn btn-secondary w-100 py-2'
        ].join(' ')}
        onClick={() => {
            // if you made changes to the formik, show a warning first via popup
            const obj = treatmentEditFormik.touched

            interface FormikTouched {
                [key: string]: boolean;
              }

            function hasTrueValue (obj: FormikTouched) {
                for (const key in obj) {
                    if (obj[key] === true) {
                        return true
                    }
                }
                return false
            }

            const hasTrue = hasTrueValue(obj)
            if (hasTrue) {
                // show warning pls
                setShowCancelWarning(true)
            } else {
                // go back to view
                const isValid = validateRoute(
                    activeModules.arr,
                    MODULE_TABLE.fmt.moduleName,
                    MODULE_TABLE.fmt.routes.viewTreatment,
                    true
                )

                setModeButtonClicked(true)

                dispatch(push(
                    _.replace(isValid.route,
                        ':treatmentId',
                        treatmentId || ''
                    )
                ))
            }
        }}>
        {strings.app?.text.cancel}
    </button>
    const LoadingContent = (
        <div className={'spinner-container'}>
            <span className={'spinner-border spinner-border-sm'}></span>
            <span className={'ms-2'}>{
                strings.app?.text.loading || ''
            }</span>
        </div>
    )
    const SubmitButton = <button type={'button'}
        disabled={
        // step 1 shouldn't be disabled here because of formik.
            false
        // step 2 can be skipped entirely.
        }
        className={[
            'btn btn-primary w-100 fw-semibold py-2'
        ].join(' ')}
        onClick={() => {
            treatmentEditFormik.handleSubmit()
        }}>
        { editTreatmentMutation.isLoading
            ? LoadingContent
            : strings.app?.text.save}
    </button>

    const EditButton = <button type={'button'}
        className={[
            'btn btn-secondary w-100 py-2'
        ].join(' ')}
        onClick={() => {
            const isValid = validateRoute(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.routes.editTreatment,
                true
            )

            setModeButtonClicked(true)

            dispatch(push(
                _.replace(isValid.route,
                    ':treatmentId',
                    treatmentId || ''
                )
            ))
        }}>
        {strings.fmt?.text.department.details.edit}
    </button>

    const ChangeProfilePictureButton = <>
        <input
            type={'file'}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
        />
        <button type={'button'}
            className={[
                'btn btn-secondary w-100 mt-5'
            ].join(' ')}
            onClick={() => {
                if (fileInputRef.current) {
                    fileInputRef.current.click()
                }
            }}>
            { editTreatmentMutation.isLoading
                ? LoadingContent
                : strings.fmt?.text.department.details.change}
        </button>
    </>

    /** display list of treatments here with the data table template. */

    const result = <div className={'row my-8'}>
        <div className={'col-12 col-lg'}>
            <div className={'edit-interface'}>
                <h2 className={'mb-5 fw-semibold '} >{strings.fmt?.text.facility.add.treatment}</h2>
                {/* group one */}
                <div className={'mb-6'}>
                    <h4 className={'mb-6'}>{strings.fmt
                        ?.text.facility.add.steps['3'].details}</h4>
                    {NameInput}
                </div>

                {mode === 'EDIT' && <div className={'container button-container'}>
                    <div className={'row align-items-center mt-6'}>
                        <div className={'col-6 col-lg-4 col-md-6 offset-lg-4 offset-md-0'}>
                            {CancelButton}
                        </div>
                        <div className={'col-6 col-lg-4 col-md-6'}>
                            {SubmitButton}
                        </div>
                    </div>
                </div>}

                {mode === 'VIEW' && <div className={'container button-container'}>
                    <div className={'align-items-center justify-content-end mt-6 row'}>
                        <div className={'col-6 col-lg-4 col-md-6'}>
                            {EditButton}
                        </div>
                    </div>
                </div>}
            </div>
        </div>
        <div className={'col-12 col-lg-auto mt-6 mt-lg-0'}>
            <div className={'picture-interface'}>
                <h2 className={'mb-5 fw-semibold '} >{strings.fmt?.text
                    .department.details.image}</h2>
                <div className={'text-center'}>

                    <div className={'img-wrapper mx-auto mb-2'}>

                        {<ProfilePic
                            showRecentlyUploaded={showRecentlyUploaded}
                            updateProfilePictureTreatmentMutation={{
                                data: updateProfilePictureTreatmentMutation.data
                            }}
                            getTreatmentMutation={getTreatmentMutation}
                        />}

                    </div>
                    {
                        mode === 'EDIT' && ChangeProfilePictureButton
                    }
                </div>
            </div>
        </div>

    </div>

    return <div className={'treatment-page'}>

        {/* dashboard responsive header */}
        <DashboardResponsiveHeader />

        <div className={'main-content'}>

            <div className={'container-fluid mx-auto'}>
                <div className={'row justify-content-between pt-16 pb-0 px-5'}>
                    <div className={'col-auto'}>
                        <a onClick={(e) => {
                            e.preventDefault()
                            dispatch(back())
                        }} className={'btn btn-round btn-sm-md'}>
                            <i
                                className={'fa-light fa-arrow-left'}
                                aria-hidden={'true'}>
                            </i>
                        </a>
                    </div>
                    <div className={'col-auto'}>
                        <h1 className={'my-0'}>{
                            getTreatmentMutation.data?.data.treatmentName
                        }</h1>
                    </div>
                    <div className={'col-auto'}>
                        <a href={'#'}
                            onClick={(e) => {
                                e.preventDefault()
                            }} className={[
                                'btn btn-round btn-sm-md invisible'
                            ].join(' ')}>
                            <i
                                className={'fa-light fa-arrow-right'}
                                aria-hidden={'true'}
                            >
                            </i>
                        </a>
                    </div>
                </div>
                <div className={'row justify-content-between mt-5'}>
                    <div className={'col'}>
                        <div className={
                            'container-fluid px-5 px-sm-3 treatment-body'
                        }>
                            <div className={'h-100 row'}>
                                <div className={' col-12 mx-auto'}>
                                    {result}
                                </div>
                            </div>
                        </div>

                        <div className={'container-fluid px-5 px-sm-3 pb-16 ' +
                        'treatment-body list-interface'}>
                            <div className={'h-100 row'}>
                                <div className={' col-12 mx-auto'}>
                                    <h5 className={'fw-semibold'}>{strings.fmt
                                        ?.text.carepath.carepath}</h5>
                                    <CarepathsViewTable
                                        mode={mode}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        responseData={getTreatmentCarepathsResponse}
                                        fixedCacheKey={fixedCacheKey}
                                    />
                                </div>
                            </div>
                        </div>

                        <Modal centered={true} isOpen={showCancelWarning} toggle={() => {
                            setShowCancelWarning(!showCancelWarning)
                        }}>
                            <ModalHeader className={'justify-content-between'} toggle={() => {
                                setShowCancelWarning(!showCancelWarning)
                            }} close={ <a className={'btn btn-round '}
                                onClick={(e) => {
                                    e.preventDefault()
                                    setShowCancelWarning(false)
                                }}>
                                <i className={'fa-light fa-multiply'} aria-hidden={'true'}>
                                </i>
                            </a>}
                            >
                                <span className={'h5'}>{
                                    strings.fmt?.message?.data_edit_warning?.title || ''
                                }</span>
                            </ModalHeader>
                            <ModalBody>
                                {/* content changes if idle / expire sessions expired. */}
                                <small className={'d-block my-4'}>
                                    {strings.fmt?.message?.data_edit_warning?.content || ''}
                                </small>
                                <div className={'row justify-content-end'}>
                                    <div className={'col-auto mb-2 mb-md-0'}>
                                        <button
                                            type={'button'}
                                            className={'btn btn-primary w-100'}
                                            onClick={ () => {
                                                setShowCancelWarning(false)
                                            }}>
                                            {strings.app?.text?.no || ''}
                                        </button>
                                    </div>
                                    <div className={'col-auto'}>
                                        <button type={'button'} onClick={() => {
                                            const isValid = validateRoute(
                                                activeModules.arr,
                                                MODULE_TABLE.fmt.moduleName,
                                                MODULE_TABLE.fmt.routes.viewTreatment,
                                                true
                                            )

                                            setModeButtonClicked(true)

                                            dispatch(push(
                                                _.replace(isValid.route,
                                                    ':treatmentId',
                                                    treatmentId || ''
                                                )
                                            ))

                                            setShowCancelWarning(false)
                                        }} className={'btn btn-primary w-100'}>
                                            {strings.app?.text.yes}
                                        </button>

                                    </div>
                                </div>
                            </ModalBody>
                        </Modal>

                    </div>
                </div>

            </div>

        </div>

        <div
            className={'position-fixed bottom-0 end-0 pe-5 fs-label fw-light version-text'}
        >
            {MODULE_VERSION}
        </div>
    </div>
}

export default TreatmentDetailsInterface
